import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Button } from 'reactstrap';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import Sidebar from '../../Sidebar';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import FloatingControls from '../../components/FloatingControls';
import { displayError } from '../../helpers';
import {
    cancelGeneralJournal,
    deleteGeneralJournal,
    getGeneralJournalDetails,
} from '../../services/GeneralJournalServices';
import { AccountingHintPanel } from './VoucherUtils';
import { fetchAndShowGeneralJournalPdf } from '../../utils/generalJournalPdf';

function GeneralJournalEdit() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [record, setRecord] = useState(null);
    const [inProgress, setInProgress] = useState(true);

    useEffect(() => {
        if (!id) {
            return;
        }
        setInProgress(true);
        getGeneralJournalDetails(cookies, id)
            .then((response) => setRecord(response))
            .catch((error) => displayError(error?.response?.data || error, 'Load Failed'))
            .finally(() => setInProgress(false));
    }, [cookies, id]);

    const handlePrint = () => {
        setInProgress(true);
        fetchAndShowGeneralJournalPdf(cookies, id, { title: 'General Journal' })
            .finally(() => setInProgress(false));
    };

    const handleCancel = () => {
        Swal.fire({
            title: 'Cancel general journal?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            cancelGeneralJournal(cookies, id)
                .then((response) => {
                    Swal.fire('Cancelled', response.message || 'General journal cancelled.', 'success');
                    return getGeneralJournalDetails(cookies, id);
                })
                .then((response) => setRecord(response))
                .catch((error) => displayError(error?.response?.data || error, 'Cancel Failed'))
                .finally(() => setInProgress(false));
        });
    };

    const handleDelete = () => {
        Swal.fire({
            title: 'Delete cancelled general journal?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            deleteGeneralJournal(cookies, id)
                .then((response) => {
                    Swal.fire('Deleted', response.message || 'General journal deleted.', 'success');
                    navigate('/GeneralJournalTable');
                })
                .catch((error) => displayError(error?.response?.data || error, 'Delete Failed'))
                .finally(() => setInProgress(false));
        });
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <FormPageLayout
                    moduleLabel="ACCOUNTS"
                    title="General Journal"
                    subtitle={record?.journal_no || ''}
                    modeLabel="VIEW MODE"
                >
                    {record && (
                        <>
                            <AccountingHintPanel hint={record.accounting_hint} />
                            <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                                <Col sm={3} md={3}>
                                    <strong className="d-block">Plant</strong>
                                    <div>{record.plant_alias}</div>
                                </Col>
                                <Col sm={3} md={3}>
                                    <strong className="d-block">Journal No</strong>
                                    <div>{record.journal_no}</div>
                                </Col>
                                <Col sm={3} md={3}>
                                    <strong className="d-block">Journal Date</strong>
                                    <div>{record.journal_date}</div>
                                </Col>
                                <Col sm={3} md={3}>
                                    <strong className="d-block">Status</strong>
                                    <div>{record.status ? 'Active' : 'Cancelled'}</div>
                                </Col>
                            </Row>
                            <div className="form-row table-bordered shadow p-3 my-2 border-secondary form-control-panel table-responsive">
                                    <table className="table table-sm table-bordered">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Account</th>
                                                <th className="text-right">Debit</th>
                                                <th className="text-right">Credit</th>
                                                <th>Line Narration</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(record.journal_lines || []).map((line, index) => (
                                                <tr key={line.id || index}>
                                                    <td>{index + 1}</td>
                                                    <td>{line.account_name}</td>
                                                    <td className="text-right">{line.debit_amt ? Number(line.debit_amt).toFixed(2) : ''}</td>
                                                    <td className="text-right">{line.credit_amt ? Number(line.credit_amt).toFixed(2) : ''}</td>
                                                    <td>{line.line_narration || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <th colSpan={2} className="text-right">Totals</th>
                                                <th className="text-right">{Number(record.total_debit || 0).toFixed(2)}</th>
                                                <th className="text-right">{Number(record.total_credit || 0).toFixed(2)}</th>
                                                <th />
                                            </tr>
                                        </tfoot>
                                    </table>
                            </div>
                            <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                                <Col sm={12}>
                                    <strong className="d-block mb-1">Narration / Remarks</strong>
                                    <div>{record.remarks || '—'}</div>
                                </Col>
                            </Row>
                            <Row className="form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                                <Col className="d-flex justify-content-end flex-wrap">
                                    {record?.status ? (
                                        <>
                                            <Button
                                                color="primary"
                                                type="button"
                                                className="mr-2 mb-2"
                                                onClick={() => navigate(`/GeneralJournal/edit/${id}`)}
                                            >
                                                Edit
                                            </Button>
                                            <Button color="warning" type="button" className="mr-2 mb-2" onClick={handleCancel}>
                                                Cancel Voucher
                                            </Button>
                                        </>
                                    ) : (
                                        <Button color="danger" type="button" className="mr-2 mb-2" onClick={handleDelete}>
                                            Delete
                                        </Button>
                                    )}
                                    <Button color="success" type="button" className="mr-2 mb-2" onClick={handlePrint}>
                                        Print
                                    </Button>
                                    <Button color="secondary" type="button" className="mb-2" onClick={() => navigate('/GeneralJournalTable')}>
                                        Back to List
                                    </Button>
                                </Col>
                            </Row>
                        </>
                    )}
                </FormPageLayout>
                <LoadingOverlay active={inProgress} />
                <FloatingControls tableLink="/GeneralJournalTable" enableCancel={false} />
            </div>
        </div>
    );
}

export default GeneralJournalEdit;
